import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { dato } from '../lib/format';
import { PageHeader, Card, Modal } from '../components/ui';
import { Upload, Download, Trash2, FileText, Filter } from 'lucide-react';

const KATEGORIER = ['avtale', 'kontrakt', 'forsikring', 'policy', 'annet'];

const formaterStorrelse = (b) => {
  if (!b) return '0';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} kB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const Dokumenter = () => {
  const [filter, setFilter] = useState('');
  const [data, setData] = useState([]);
  const [kontakter, setKontakter] = useState([]);
  const [open, setOpen] = useState(false);
  const [fil, setFil] = useState(null);
  const [skjema, setSkjema] = useState({ kategori: 'avtale', beskrivelse: '', kontakt_id: '' });
  const [busy, setBusy] = useState(false);
  const [feil, setFeil] = useState('');

  const last = useCallback(() => {
    api.get('/dokumenter', { params: filter ? { kategori: filter } : {} })
      .then((r) => setData(r.data));
  }, [filter]);
  useEffect(() => { last(); }, [last]);
  useEffect(() => { api.get('/kontakter').then((r) => setKontakter(r.data || [])).catch(() => {}); }, []);

  const lastNed = (d) => {
    const url = `${api.defaults.baseURL}/dokumenter/${d.id}/last-ned`;
    const tok = localStorage.getItem('token');
    fetch(url, { headers: { Authorization: `Bearer ${tok}` } })
      .then((r) => r.blob())
      .then((b) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(b); a.download = d.filnavn; a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      });
  };

  const slett = async (d) => {
    if (!window.confirm(`Slett "${d.filnavn}"?`)) return;
    await api.delete(`/dokumenter/${d.id}`); last();
  };

  const lagre = async () => {
    if (!fil) { setFeil('Velg en fil.'); return; }
    setBusy(true); setFeil('');
    try {
      const fd = new FormData();
      fd.append('fil', fil);
      fd.append('kategori', skjema.kategori);
      fd.append('beskrivelse', skjema.beskrivelse);
      if (skjema.kontakt_id) fd.append('kontakt_id', skjema.kontakt_id);
      await api.post('/dokumenter', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setOpen(false); setFil(null);
      setSkjema({ kategori: 'avtale', beskrivelse: '', kontakt_id: '' });
      last();
    } catch (e) {
      setFeil(e.response?.data?.error || 'Feil ved opplasting.');
    } finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader
        title="Dokumentarkiv"
        subtitle="Avtaler, kontrakter, forsikringer og policyer"
        actions={<button className="btn-primary" onClick={() => setOpen(true)}>
          <Upload size={14} /> Last opp
        </button>}
      />

      <Card style={{ marginBottom: 12 }}>
        <div className="flex items-center" style={{ gap: 10 }}>
          <Filter size={14} />
          <label style={{ margin: 0 }}>Kategori:</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: 200 }}>
            <option value="">Alle</option>
            {KATEGORIER.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
      </Card>

      <Card style={{ padding: 0 }}>
        <table>
          <thead>
            <tr>
              <th>Filnavn</th><th>Kategori</th><th>Beskrivelse</th>
              <th>Størrelse</th><th>Opplastet</th><th style={{ width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && (
              <tr><td colSpan={6} className="fm-text-muted" style={{ textAlign: 'center', padding: 20 }}>
                Ingen dokumenter.
              </td></tr>
            )}
            {data.map((d) => (
              <tr key={d.id}>
                <td><FileText size={12} /> {d.filnavn}</td>
                <td><span className="badge badge-neutral">{d.kategori}</span></td>
                <td>{d.beskrivelse || <span className="fm-text-muted">—</span>}</td>
                <td>{formaterStorrelse(d.storrelse)}</td>
                <td>{d.opplastet ? dato(d.opplastet.slice(0, 10)) : ''}</td>
                <td>
                  <div className="flex" style={{ gap: 4 }}>
                    <button className="btn-ghost" onClick={() => lastNed(d)} title="Last ned"><Download size={12} /></button>
                    <button className="btn-ghost" onClick={() => slett(d)} title="Slett"
                            style={{ color: 'var(--theme-danger)' }}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Modal
        open={open} onClose={() => setOpen(false)}
        title="Last opp dokument"
        footer={<>
          <button className="btn-ghost" onClick={() => setOpen(false)}>Avbryt</button>
          <button className="btn-primary" onClick={lagre} disabled={busy}>
            {busy ? 'Laster opp…' : 'Last opp'}
          </button>
        </>}
      >
        <div className="grid" style={{ gridTemplateColumns: '1fr', gap: 10 }}>
          <div>
            <label>Fil</label>
            <input type="file" onChange={(e) => setFil(e.target.files?.[0] || null)} />
          </div>
          <div>
            <label>Kategori</label>
            <select value={skjema.kategori} onChange={(e) => setSkjema({...skjema, kategori: e.target.value})}>
              {KATEGORIER.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          <div>
            <label>Tilknyttet kontakt (valgfri)</label>
            <select value={skjema.kontakt_id}
                    onChange={(e) => setSkjema({...skjema, kontakt_id: e.target.value})}>
              <option value="">— ingen —</option>
              {kontakter.map((k) => (
                <option key={k.id} value={k.id}>{k.navn}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Beskrivelse</label>
            <textarea rows={3} value={skjema.beskrivelse}
                      onChange={(e) => setSkjema({...skjema, beskrivelse: e.target.value})} />
          </div>
          {feil && <div style={{ color: 'var(--theme-danger)', fontSize: 12 }}>{feil}</div>}
        </div>
      </Modal>
    </>
  );
};

export default Dokumenter;
